import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Api, BackfillBucket, ActivityTask, AiHealthSnapshot } from "../api/client";
import {
  GaugeCircle, RefreshCw, AlertTriangle, CheckCircle2, Settings as SettingsIcon, Sparkles, Activity, Save,
} from "lucide-react";
import { format, parseISO } from "date-fns";

const KIND_LABELS: Record<string, string> = {
  Pipeline1Sign: "Pipeline1 знак",
  Pipeline1Date: "Pipeline1 дата",
  DzenBuildDate: "Дзен build",
  BundleDate: "Bundle (Pipeline2)",
  CollectiveContext: "Astro context",
  BackfillRun: "Backfill run",
  DailyPublish: "Daily publish",
  CaptchaSolve: "Captcha solve",
  ImageGenerate: "Image gen",
  Other: "Other",
};

export function Readiness() {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(true);

  const status = useQuery({
    queryKey: ["backfill-status"],
    queryFn: Api.backfillStatus,
    refetchInterval: autoRefresh ? 15_000 : false,
  });
  const settings = useQuery({
    queryKey: ["generation-settings"],
    queryFn: Api.generationSettings,
    refetchInterval: autoRefresh ? 10_000 : false,
  });
  const activity = useQuery({
    queryKey: ["backfill-activity"],
    queryFn: Api.backfillActivity,
    refetchInterval: autoRefresh ? 3_000 : false,
  });

  const mRunBackfill = useMutation({
    mutationFn: Api.backfillRun,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["backfill-status"] }),
  });

  if (status.isLoading) {
    return <p className="text-white/40 text-sm">Загружаем готовность…</p>;
  }
  if (status.error || !status.data) {
    return (
      <div className="card p-4 bg-rose-500/10 border-rose-500/30 text-rose-200 text-sm">
        Ошибка: {String((status.error as Error)?.message ?? "no data")}
      </div>
    );
  }

  const s = status.data;

  const overallMin = Math.round(
    ((s.signDayCore.readyMin + s.dzenArticles.readyMin + s.socialBundles.readyMin) /
      Math.max(1, s.signDayCore.totalMin + s.dzenArticles.totalMin + s.socialBundles.totalMin)) *
      100
  );
  const overallMax = Math.round(
    ((s.signDayCore.readyMax + s.dzenArticles.readyMax + s.socialBundles.readyMax) /
      Math.max(1, s.signDayCore.totalMax + s.dzenArticles.totalMax + s.socialBundles.totalMax)) *
      100
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <GaugeCircle size={28} className="text-astro-violet" />
          <h1 className="font-display text-3xl font-bold">Готовность контента</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-white/50">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            авто-обновление
          </label>
          <button
            onClick={() => { status.refetch(); activity.refetch(); }}
            className="btn-secondary"
            disabled={status.isFetching}
          >
            <RefreshCw size={14} className={status.isFetching ? "animate-spin" : ""} />
          </button>
          <button
            onClick={() => mRunBackfill.mutate()}
            disabled={mRunBackfill.isPending}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Sparkles size={14} className={mRunBackfill.isPending ? "animate-spin" : ""} />
            {mRunBackfill.isPending ? "Backfill идёт…" : "Запустить backfill сейчас"}
          </button>
        </div>
      </div>

      {/* === AI server health === */}
      {activity.data?.aiHealth && activity.data.aiHealth.state !== "Closed" && (
        <AiHealthBanner ai={activity.data.aiHealth} />
      )}

      {/* === LIVE ACTIVITY === */}
      <ActivityPanel activity={activity.data} />

      {/* === SETTINGS form === */}
      {settings.data && (
        <SettingsForm initial={settings.data} onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["generation-settings"] });
          queryClient.invalidateQueries({ queryKey: ["backfill-status"] });
        }} />
      )}

      {/* Run report */}
      {mRunBackfill.data && (
        <div className="card p-4 bg-emerald-500/5 border-emerald-500/20 text-sm">
          <div className="flex items-center gap-2 text-emerald-300 mb-1">
            <CheckCircle2 size={14} />
            <strong>Backfill отработал</strong>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-white/70">
            <span>Pipeline1: <strong>{mRunBackfill.data.pipeline1Generated}</strong></span>
            <span>Дзен: <strong>{mRunBackfill.data.dzenArticlesBuilt}</strong></span>
            <span>Bundles: <strong>{mRunBackfill.data.bundlesGenerated}</strong></span>
            <span>Failed: <strong className={mRunBackfill.data.failed > 0 ? "text-rose-300" : ""}>{mRunBackfill.data.failed}</strong></span>
            <span>Cost: <strong>{mRunBackfill.data.totalCostRub.toFixed(4)} ₽</strong></span>
          </div>
          {mRunBackfill.data.errorMessage && (
            <div className="text-xs text-rose-300/70 mt-2">{mRunBackfill.data.errorMessage}</div>
          )}
        </div>
      )}
      {mRunBackfill.error && (
        <div className="card p-3 bg-rose-500/10 border-rose-500/30 text-rose-200 text-sm">
          Ошибка backfill: {String((mRunBackfill.error as Error).message)}
        </div>
      )}

      {/* Overall summary */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryGauge
          label="Малый круг"
          subtitle={`${s.minHorizonDays} дней приоритетного горизонта`}
          percent={overallMin}
          color="emerald"
        />
        <SummaryGauge
          label="Большой круг"
          subtitle={`${s.maxHorizonDays} дней максимум`}
          percent={overallMax}
          color="violet"
        />
      </section>

      {/* Per-bucket */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <BucketCard label="Sign Day Core (Pipeline1)" bucket={s.signDayCore}
          subtitle="12 знаков на дату" />
        <BucketCard label="Дзен — пост дня (AI-landing)" bucket={s.dzenLandings}
          subtitle="1 объединённый пост/день" />
        <BucketCard label="Соц.bundle" bucket={s.socialBundles}
          subtitle="1 на дату (TG/VK/OK/MAX)" />
        <CompatibilityCard compat={s.compatibility} />
        <BucketCard label="Дзен статьи (источник)" bucket={s.dzenArticles}
          subtitle="12 пер-знак — источник поста дня" />
      </section>

      {/* Missing in min — alarm зона */}
      <section className="card p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-amber-400" />
          <h2 className="font-display text-lg font-semibold">Не хватает в малом круге (приоритет)</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <MissingList label="Sign Day Core" dates={s.signDayCore.missingInMin} />
          <MissingList label="Дзен — пост дня" dates={s.dzenLandings.missingInMin} />
          <MissingList label="Bundle" dates={s.socialBundles.missingInMin} />
          <MissingList label={`Compat (<${s.compatibility.pairsPerDay} пар)`}
            dates={s.compatibility.incompleteInMin} />
        </div>
      </section>
    </div>
  );
}

/**
 * Карточка для совместимости пар (Lunara). Отличается от обычного BucketCard тем что
 * метрика составная: «N дней полных» И «X из Y пар». Один знаменатель не годится —
 * день с 77/78 пар не "готов", но рядом полезно видеть и общий прогресс по парам.
 */
function CompatibilityCard({ compat }: { compat: import("../api/client").CompatibilityBucket }) {
  const pctDaysMin = compat.totalDaysMin > 0
    ? (compat.fullDaysMin / compat.totalDaysMin) * 100 : 0;
  const pctDaysMax = compat.totalDaysMax > 0
    ? (compat.fullDaysMax / compat.totalDaysMax) * 100 : 0;
  const pctPairsMin = compat.pairsTotalMin > 0
    ? (compat.pairsReadyMin / compat.pairsTotalMin) * 100 : 0;

  return (
    <div className="card p-4 space-y-3">
      <div>
        <div className="text-xs uppercase tracking-wide text-zinc-400">Совместимость дня (Lunara)</div>
        <div className="text-sm text-zinc-500">{compat.pairsPerDay} пар на дату (78 = все 12×12 канонических)</div>
      </div>

      {/* Малый круг — основная метрика для prod-готовности */}
      <div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-zinc-300">Малый круг (дни)</span>
          <span className="font-mono tabular-nums">
            <strong className={pctDaysMin >= 100 ? "text-emerald-400" : pctDaysMin >= 50 ? "text-amber-400" : "text-rose-400"}>
              {compat.fullDaysMin}
            </strong>
            <span className="text-zinc-500"> / {compat.totalDaysMin}</span>
          </span>
        </div>
        <div className="h-1.5 mt-1 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-emerald-500/70 transition-all"
            style={{ width: `${pctDaysMin}%` }} />
        </div>
        <div className="text-xs text-zinc-500 mt-1">
          {compat.pairsReadyMin.toLocaleString()} / {compat.pairsTotalMin.toLocaleString()} пар ({pctPairsMin.toFixed(0)}%)
        </div>
      </div>

      {/* Большой круг — overview, тот же прогресс что в bundles/dzen */}
      <div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-zinc-300">Большой круг (дни)</span>
          <span className="font-mono tabular-nums text-zinc-300">
            {compat.fullDaysMax} / {compat.totalDaysMax}
          </span>
        </div>
        <div className="h-1.5 mt-1 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-violet-500/60 transition-all"
            style={{ width: `${pctDaysMax}%` }} />
        </div>
      </div>
    </div>
  );
}

const CODE_LABELS: Record<string, { title: string; cause: string; severity: "warn" | "critical" }> = {
  proxy_unreachable: {
    title: "Прокси недоступны",
    cause: "Все мобильные модемы либо в середине ротации, либо carrier временно отказывает. Часто означает «белый список» / регулирование мобильного интернета — обычно проходит за 5–15 мин.",
    severity: "warn",
  },
  accounts_exhausted: {
    title: "Qwen-аккаунты исчерпаны",
    cause: "У всех аккаунтов в aiserver-БД закончилась quota или они забанены. Требуется ручная заправка / замена ключей.",
    severity: "critical",
  },
  accounts_quota_reached: {
    title: "Аккаунты упёрлись в rate-limit",
    cause: "Все аккаунты hit лимит на запросы. Свежие quota обычно появляются в течение нескольких минут.",
    severity: "warn",
  },
  regional_block: {
    title: "Региональная блокировка",
    cause: "Aiserver обнаружил блокировку маршрута до Alibaba (DNS/route не резолвится). Скорее всего «белый список» РФ.",
    severity: "critical",
  },
  upstream_down: {
    title: "Alibaba API лежит",
    cause: "Внешний Qwen API отдаёт 5xx. Обычно короткий внешний инцидент.",
    severity: "warn",
  },
};

function AiHealthBanner({ ai }: { ai: AiHealthSnapshot }) {
  const isExtended = ai.isExtendedOutage || ai.state === "ExtendedOpen";
  const outageMinutes = ai.outageStartedAt
    ? Math.round((Date.now() - new Date(ai.outageStartedAt).getTime()) / 60_000)
    : 0;
  const codeInfo = ai.lastFailureCode ? CODE_LABELS[ai.lastFailureCode] : undefined;
  const isCritical = isExtended || codeInfo?.severity === "critical";

  return (
    <div className={`card p-4 text-sm ${
      isCritical
        ? "bg-rose-500/10 border-rose-500/50"
        : "bg-amber-500/10 border-amber-500/40"
    }`}>
      <div className={`flex items-center gap-2 mb-1 flex-wrap ${isCritical ? "text-rose-300" : "text-amber-300"}`}>
        <AlertTriangle size={16} />
        <strong>
          {codeInfo?.title ?? (isExtended ? "AI server: длительный outage" : "AI server временно недоступен")}
        </strong>
        {ai.lastFailureCode && (
          <span className={`px-2 py-0.5 rounded text-xs font-mono ${isCritical ? "bg-rose-500/20" : "bg-amber-500/20"}`}>
            {ai.lastFailureCode}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-xs ${isCritical ? "bg-rose-500/20" : "bg-amber-500/20"}`}>
          circuit {ai.state}
        </span>
        {outageMinutes > 0 && (
          <span className="text-xs opacity-70">{outageMinutes} мин outage</span>
        )}
      </div>

      {/* Health-метрики (если aiserver их прислал) */}
      {(ai.proxiesTotal != null || ai.accountsTotal != null) && (
        <div className="flex items-center gap-4 mt-2 text-xs">
          {ai.proxiesTotal != null && (
            <div className="flex items-center gap-1">
              <span className="text-white/40">Прокси:</span>
              <span className={`font-mono font-semibold ${
                (ai.proxiesHealthy ?? 0) === 0 ? "text-rose-300"
                  : (ai.proxiesHealthy ?? 0) < ai.proxiesTotal / 2 ? "text-amber-300"
                  : "text-emerald-300"
              }`}>
                {ai.proxiesHealthy ?? 0} / {ai.proxiesTotal}
              </span>
            </div>
          )}
          {ai.accountsTotal != null && (
            <div className="flex items-center gap-1">
              <span className="text-white/40">Аккаунты:</span>
              <span className={`font-mono font-semibold ${
                (ai.accountsHealthy ?? 0) === 0 ? "text-rose-300"
                  : (ai.accountsHealthy ?? 0) < ai.accountsTotal / 2 ? "text-amber-300"
                  : "text-emerald-300"
              }`}>
                {ai.accountsHealthy ?? 0} / {ai.accountsTotal}
              </span>
            </div>
          )}
        </div>
      )}

      <div className={`text-xs mt-2 ${isCritical ? "text-rose-200/80" : "text-amber-200/80"}`}>
        {ai.lastFailureReason && <div>Сообщение: {ai.lastFailureReason}</div>}
        {ai.openUntil && (
          <div className="mt-1">
            {isExtended ? "Cooldown до" : "Probe запустится"}:{" "}
            <span className="font-mono">{new Date(ai.openUntil).toLocaleTimeString("ru-RU")}</span>
          </div>
        )}
      </div>

      {codeInfo?.cause && (
        <div className="text-xs text-white/60 mt-2 leading-relaxed">
          <strong className="text-white/80">Диагноз:</strong> {codeInfo.cause}
        </div>
      )}

      <div className="text-xs text-white/50 mt-2">
        {isExtended
          ? "Backfill приостановлен. Probe /v1/models идёт раз в 30 сек — как только aiserver вернётся, восстановимся автоматически."
          : "Backfill приостановлен на короткий cooldown. Worker и публикация работают штатно."}
      </div>
    </div>
  );
}

function ActivityPanel({ activity }: { activity?: { activeCount: number; activeTasks: ActivityTask[]; recentlyCompleted: ActivityTask[] } }) {
  const active = activity?.activeTasks ?? [];
  const recent = activity?.recentlyCompleted ?? [];
  return (
    <section className="card p-5 border-l-4 border-l-astro-violet">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={18} className={active.length > 0 ? "text-astro-violet animate-pulse" : "text-white/40"} />
        <h2 className="font-display text-lg font-semibold">Сейчас в работе</h2>
        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${active.length > 0 ? "bg-astro-violet/20 text-astro-violet" : "bg-white/5 text-white/40"}`}>
          {active.length} активных {active.length > 0 ? "потоков" : ""}
        </span>
        {active.length === 0 && (
          <span className="text-white/40 text-xs ml-auto">простаивает</span>
        )}
      </div>

      {active.length > 0 && (
        <div className="space-y-2 mb-4">
          {active.map((t) => (
            <ActiveRow key={t.id} task={t} />
          ))}
        </div>
      )}

      {recent.length > 0 && (
        <div>
          <div className="text-white/40 text-xs uppercase mb-2">Недавно завершено ({recent.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {recent.slice(0, 12).map((t) => (
              <CompletedRow key={t.id} task={t} />
            ))}
          </div>
        </div>
      )}

      {active.length === 0 && recent.length === 0 && (
        <p className="text-white/30 text-sm italic">пока ни одной задачи не выполнялось</p>
      )}
    </section>
  );
}

function ActiveRow({ task }: { task: ActivityTask }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startedMs = new Date(task.startedAt).getTime();
  const dur = Math.round((now - startedMs) / 1000);
  return (
    <div className="bg-black/20 rounded px-3 py-2 flex items-center gap-3 text-sm">
      <RefreshCw size={12} className="text-astro-violet animate-spin shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-white/80 truncate">{task.description}</div>
        <div className="text-xs text-white/40">
          {KIND_LABELS[task.kind] ?? task.kind}
          {task.signCode ? ` · ${task.signCode}` : ""}
        </div>
      </div>
      <div className="text-xs font-mono text-white/50 shrink-0">{dur}s</div>
    </div>
  );
}

function CompletedRow({ task }: { task: ActivityTask }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {task.success ? (
        <CheckCircle2 size={11} className="text-emerald-400 shrink-0" />
      ) : (
        <AlertTriangle size={11} className="text-rose-400 shrink-0" />
      )}
      <span className="text-white/60 truncate flex-1">{task.description}</span>
      <span className="text-white/30 font-mono shrink-0">{task.durationSec.toFixed(1)}s</span>
    </div>
  );
}

function SettingsForm({ initial, onSaved }: { initial: { minHorizonDays: number; maxHorizonDays: number; backfillConcurrency: number }; onSaved: () => void }) {
  const [min, setMin] = useState(initial.minHorizonDays);
  const [max, setMax] = useState(initial.maxHorizonDays);
  const [conc, setConc] = useState(initial.backfillConcurrency);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMin(initial.minHorizonDays);
    setMax(initial.maxHorizonDays);
    setConc(initial.backfillConcurrency);
  }, [initial]);

  const mUpdate = useMutation({
    mutationFn: () => Api.updateGenerationSettings({ minHorizonDays: min, maxHorizonDays: max, backfillConcurrency: conc }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => {
      setError(String((err as Error).message));
      setSaved(false);
    },
  });

  const dirty = min !== initial.minHorizonDays || max !== initial.maxHorizonDays || conc !== initial.backfillConcurrency;

  return (
    <section className="card p-5">
      <div className="flex items-center gap-2 mb-3">
        <SettingsIcon size={18} className="text-white/60" />
        <h2 className="font-display text-lg font-semibold">Настройки горизонта</h2>
        <span className="text-white/30 text-xs ml-2">сохраняются в БД, применяются на лету</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NumberField
          label="Малый круг (приоритет)"
          subtitle="дней — должны быть закрыты всегда"
          value={min}
          min={1}
          max={60}
          onChange={setMin}
        />
        <NumberField
          label="Большой круг"
          subtitle={`дней — макс горизонт планирования (1–365)`}
          value={max}
          min={1}
          max={365}
          onChange={setMax}
        />
        <NumberField
          label="Параллельность"
          subtitle="одновременных AI-запросов (1–30)"
          value={conc}
          min={1}
          max={30}
          onChange={setConc}
        />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={() => mUpdate.mutate()}
          disabled={!dirty || mUpdate.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
        >
          <Save size={14} />
          {mUpdate.isPending ? "Сохраняем…" : "Сохранить"}
        </button>
        {saved && <span className="text-emerald-300 text-sm">✓ сохранено и применено</span>}
        {error && <span className="text-rose-300 text-sm">{error}</span>}
        {dirty && !saved && <span className="text-amber-300 text-xs">есть несохранённые изменения</span>}
      </div>
    </section>
  );
}

function NumberField({ label, subtitle, value, min, max, onChange }: {
  label: string; subtitle: string; value: number; min: number; max: number; onChange: (n: number) => void;
}) {
  return (
    <div>
      <label className="block">
        <span className="text-white/60 text-xs uppercase">{label}</span>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          className="mt-1 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-lg font-display font-bold focus:outline-none focus:border-astro-violet"
        />
        <span className="block text-xs text-white/40 mt-1">{subtitle}</span>
      </label>
    </div>
  );
}

function MissingList({ label, dates }: { label: string; dates: string[] }) {
  return (
    <div>
      <div className="text-white/60 text-xs uppercase mb-1">{label}</div>
      {dates.length === 0 ? (
        <div className="text-emerald-300/80 text-xs flex items-center gap-1">
          <CheckCircle2 size={12} /> всё закрыто
        </div>
      ) : (
        <ul className="space-y-0.5">
          {dates.map((d) => (
            <li key={d} className="text-rose-300/80 font-mono text-xs">
              {format(parseISO(d), "dd.MM.yyyy")}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SummaryGauge({
  label, subtitle, percent, color,
}: { label: string; subtitle: string; percent: number; color: "emerald" | "violet" }) {
  const colorMap = {
    emerald: "from-emerald-400 to-emerald-600",
    violet: "from-astro-violet to-purple-700",
  };
  return (
    <div className="card p-5">
      <div className="text-white/60 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-end justify-between mb-2">
        <span className="text-4xl font-display font-bold">{percent}%</span>
        <span className="text-white/40 text-xs">{subtitle}</span>
      </div>
      <div className="h-2 bg-black/40 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colorMap[color]} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function BucketCard({
  label, subtitle, bucket,
}: { label: string; subtitle: string; bucket: BackfillBucket }) {
  const minPct = Math.round((bucket.readyMin / Math.max(1, bucket.totalMin)) * 100);
  const maxPct = Math.round((bucket.readyMax / Math.max(1, bucket.totalMax)) * 100);
  const minOk = bucket.readyMin >= bucket.totalMin;

  return (
    <div className={`card p-5 border ${minOk ? "border-emerald-500/30" : "border-amber-500/30"}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display font-semibold">{label}</div>
          <div className="text-xs text-white/40">{subtitle}</div>
        </div>
        {minOk ? (
          <CheckCircle2 size={18} className="text-emerald-300" />
        ) : (
          <AlertTriangle size={18} className="text-amber-300" />
        )}
      </div>

      <div className="space-y-2">
        <ProgressLine
          label="малый круг"
          ready={bucket.readyMin}
          total={bucket.totalMin}
          percent={minPct}
          accent="emerald"
        />
        <ProgressLine
          label="большой круг"
          ready={bucket.readyMax}
          total={bucket.totalMax}
          percent={maxPct}
          accent="violet"
        />
      </div>
    </div>
  );
}

function ProgressLine({
  label, ready, total, percent, accent,
}: {
  label: string; ready: number; total: number; percent: number; accent: "emerald" | "violet";
}) {
  const color = accent === "emerald" ? "bg-emerald-400" : "bg-astro-violet";
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span className="text-white/50">{label}</span>
        <span className="text-white/80 font-mono">{ready} / {total}</span>
      </div>
      <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
